import React from 'react';
import { useLocation } from 'react-router-dom';
import { IStorefrontSection, IStorefrontSettings, IStorefrontStyle, StorefrontStyleElementType } from '../../../../models/Storefront';
import StorefrontSectionEditor from './StorefrontSectionEditor';
import { ActionButton, Callout, ContextualMenuItemType, DirectionalHint, IButtonStyles, IconButton, IContextualMenuItem, PrimaryButton, Toggle, useTheme } from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import NoSectionsDisplay from './NoSectionsDisplay';
import StorefrontSectionsListPanel from './StorefrontSectionsListPanel';
import Setting from '../../../Common/Setting';
import { ISearchElementStyle } from '../searchPageConfig';
import { fullBleedBandStyle, fullBleedFromColor, StyleRefPicker, useStyleRefPickerVisible } from '../styleControls';
import { sectionWithAppearanceOf } from './sectionStyleRef';
import { StyleRowsWidthProvider } from '../styleRows';
import { isOnPage, mergeReorderedSubset, sectionsForPage } from '../pagesConfig';
import { countLocalOverrides, getStyleRef, resolveStyleProps, styleTargetForKey, STYLE_REF_KEY } from '../stylesConfig';
import { useStorefrontStyles } from '../StorefrontStylesContext';
import { getDefaultStyleIds } from '../styleConfig';
import { isContentSectionType } from './contentSectionConfig';
import { columnFlexBasis, PACK_EPSILON, ROW_GAP, widthPctFor } from './sectionWidths';

// Width fractions, the row gap, and the packing math live in sectionWidths.ts so the section
// editor panel and the content previews can share them without closing an import cycle through
// this file (this module already imports StorefrontSectionEditor).
const sectionWidthPct = (s: IStorefrontSection): number => widthPctFor(s.width);

// The full-bleed band color a section contributes when it opts into fullWidthBackground — its own
// styleProps.backgroundColor (undefined when the flag is off or no color is set). Mirrors the shopper.
// The band color may come from a referenced Storefront Style's `band` part rather than the
// section's own styleProps, so resolve the ref before reading backgroundColor. The styles list is
// passed in (from the React context, not the module registry) so an unsaved Styles-panel edit
// re-renders this component through the normal context subscription.
const sectionBandColor = (s: IStorefrontSection, styles: IStorefrontStyle[]): string | undefined =>
    s.fullWidthBackground
        ? (resolveStyleProps(s.styleProps, 'styleProps', styles)?.backgroundColor as string | undefined)
        : undefined;

interface IStorefrontSectionsEditorProps {
    storefrontSettings: IStorefrontSettings;
    editMode?: boolean;
    sections: IStorefrontSection[];
    onSectionsChange: (newSections: IStorefrontSection[]) => void;
    onStorefrontSettingsChange?: (newSettings: IStorefrontSettings) => void;
    /** When set, replaces the default fallback product display with this message when sections is empty. */
    emptyMessage?: string;
    isDark?: boolean;
    // Opens the PAGE-level combined product-card panel (the host page editor owns it). The cards in
    // a section preview are styled by two stacked layers — this page-wide one underneath and the
    // section's own productStyleProps on top — so the card popover offers this as its footer action
    // rather than as the card's primary click. Absent = the host has no page-level card panel.
    onOpenPageCardStyle?: () => void;
    // Per-section full-bleed band backgrounds (homePageConfig.sectionBands), indexed by section
    // order. Painted across the preview canvas so the editor matches the live storefront.
    sectionBands?: ISearchElementStyle[];
    // The centered page frame (width/maxWidth/margin/padding) that section CONTENT sits in.
    //
    // Set only in full-width mode, where the sections are rendered outside the page's content frame
    // so a band can paint edge to edge across the canvas. Each band then re-applies this to its own
    // contents, which is the preview's equivalent of the shopper renderer's
    // fullBleedBand(..., constrainInner: true) — the band spans the full width, the content inside it
    // lines up with the rest of the page. Undefined = the body already holds the max width (the
    // sections are centered by it), so nothing extra is applied and layout is unchanged.
    contentFrameStyle?: React.CSSProperties;
    // The glyphs a collapsible section's header draws (homePageConfig.sectionToggleExpandIcon /
    // …CollapseIcon). Threaded as strings because they are CONTENT, not CSS — the control's styling
    // rides the page's scoped stylesheet like every other home-page element style.
    sectionToggleIcons?: { expand: string; collapse: string };
    // Localized label for the per-section "More like this" link. CONTENT, like the toggle glyphs
    // above, so it is threaded rather than read from config down here.
    moreLikeThisLabel?: string;
    // The Sections panel — the list of this page's sections, with reorder / add / delete. Owned
    // here rather than by the host because every one of those is a mutation of the section list,
    // which only this component knows how to perform (page scoping, style-ref seeding, merge-back).
    //
    // So the host's style-bar button ASKS rather than sets: a one-shot request flag this component
    // consumes and reports handled, plus the open state reported back for the button's highlight.
    // Same shape as StorefrontBannerEditor's openEditPanelRequest / onOpenEditPanelHandled.
    openPanelRequest?: boolean;
    onOpenPanelHandled?: () => void;
    onPanelOpenChange?: (open: boolean) => void;
    // False = another Home Page Style bar entry owns the right-hand surface now, so this panel gets
    // out of the way. These panels are non-blocking and anchored to the same edge, so two open at
    // once overlap. Undefined = no host is managing exclusion (the global product settings page
    // renders this editor on its own), and nothing is forced shut.
    panelActive?: boolean;

    // Custom Page editing context (a page GUID). Undefined/empty = the main homepage. Only sections
    // for this page are DISPLAYED; new sections are stamped with it. The `sections` prop stays the
    // FULL list (all pages) and is what gets batch-saved — see pagesConfig / the data-loss note.
    page?: string;
}

interface ISectionsOptionsButtonProps {
    storefrontSettings: IStorefrontSettings;
    onStorefrontSettingsChange: (newSettings: IStorefrontSettings) => void;
}

const SectionsOptionsButton: React.FC<ISectionsOptionsButtonProps> = ({ storefrontSettings, onStorefrontSettingsChange }) => {
    const [open, setOpen] = React.useState(false);
    const buttonId = useId('sections-options-btn');

    return (
        <>
            <button
                id={buttonId}
                onClick={() => setOpen(!open)}
                className='storefront-sections-options-button'
                title='Section Options'
            >
                <span className='storefront-color-picker-label'>Section Options</span>
            </button>
            {open && (
                <Callout
                    target={`#${buttonId}`}
                    onDismiss={() => setOpen(false)}
                    setInitialFocus
                    style={{ padding: '1em' }}
                    directionalHint={DirectionalHint.bottomRightEdge}
                >
                    <h3 style={{ margin: '0 0 0.75em' }}>Section Options</h3>
                    <Setting id='storefront.sections.ignoreGlobalSections'>
                    <Toggle
                        label='Ignore Global Sections'
                        inlineLabel
                        onText='On'
                        offText='Off'
                        checked={storefrontSettings.ignoreGlobalSections ?? false}
                        onChange={(_, checked) => onStorefrontSettingsChange({ ...storefrontSettings, ignoreGlobalSections: checked ?? false })}
                    />
                    </Setting>
                </Callout>
            )}
        </>
    );
};

/** What the card popover is anchored to: which section's cards, and the exact card element clicked. */
interface ICardStylePopoverState {
    sectionId: string;
    anchor: HTMLElement;
}

// The quick style popover behind a product card in a section preview.
//
// A card inside a section is painted by the SECTION's `productStyleProps` (layered over the
// page-level card config), so the card's own affordance has to lead HERE — the page-level panel is a
// different layer and used to be what the card pencil opened, which is exactly the wrong parent.
// Everything above the divider is scoped to this one section; the page-wide layer stays reachable,
// but only behind an explicitly-labelled footer action so the two can't be confused.
//
// Deliberately small: switching the named style is the 95% case and repaints the preview instantly
// (the section update flows straight back through onSectionsChange). Anything more — raw CSS
// overrides — hands off to the full section editor rather than growing a second editing surface.
const SectionCardStylePopover: React.FC<{
    section: IStorefrontSection;
    anchor: HTMLElement;
    onChange: (section: IStorefrontSection) => void;
    onDismiss: () => void;
    /** Open the full section editor on Appearance with the Product Cards group focused. */
    onAdvanced: () => void;
    onOpenPageCardStyle?: () => void;
}> = ({ section, anchor, onChange, onDismiss, onAdvanced, onOpenPageCardStyle }) => {
    const theme = useTheme();
    const target = styleTargetForKey('productStyleProps');
    // The picker renders nothing when the org has no card styles or the user can't administer them.
    // Ask first, so the popover can say why it's empty instead of just being empty.
    const pickerVisible = useStyleRefPickerVisible(target);
    const overrideCount = countLocalOverrides(section.productStyleProps);
    const actionStyles: IButtonStyles = { root: { height: 30, padding: 0 }, label: { fontSize: 12, fontWeight: 400 } };

    return (
        <Callout
            target={anchor}
            onDismiss={onDismiss}
            setInitialFocus
            gapSpace={8}
            directionalHint={DirectionalHint.bottomCenter}
        >
            <div style={{ padding: '0.75em 1em', width: 260, boxSizing: 'border-box' }}>
                {/* Scope line: identical cards appear on the page from two different style layers,
                    so the popover states which one it edits before showing any control. */}
                <div style={{ fontSize: 13, fontWeight: 600 }}>Product Cards</div>
                <div style={{ fontSize: 11, color: theme.palette.neutralSecondary, marginBottom: '0.5em' }}>This section</div>
                {pickerVisible
                    ? (
                        // Narrower control column than either panel — this is a 260px popover.
                        <StyleRowsWidthProvider width={130}>
                            <StyleRefPicker
                                rowLayout
                                value={section.productStyleProps}
                                onChange={(patch) => onChange({ ...section, productStyleProps: { ...section.productStyleProps, ...patch } })}
                                target={target}
                            />
                        </StyleRowsWidthProvider>
                    )
                    : <div style={{ fontSize: 12, color: theme.palette.neutralSecondary }}>No saved card styles yet.</div>}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <ActionButton
                        iconProps={{ iconName: 'Settings' }}
                        text={overrideCount > 0 ? `Advanced overrides (${overrideCount})…` : 'Advanced overrides…'}
                        title="Open this section's Appearance settings with the product card overrides expanded."
                        onClick={onAdvanced}
                        styles={actionStyles}
                    />
                    {/* Quick "back to the style as designed": drop the overrides but keep the
                        $styleRef (same semantics as the Clear in CssCopyPasteButtons / the
                        overrides fold), without leaving the popover. */}
                    {overrideCount > 0 && <IconButton
                        iconProps={{ iconName: 'ClearFormatting' }}
                        title={`Clear the ${overrideCount} override${overrideCount === 1 ? '' : 's'} — revert to the style as designed`}
                        ariaLabel='Clear overrides'
                        onClick={() => {
                            const ref = getStyleRef(section.productStyleProps);
                            onChange({ ...section, productStyleProps: ref ? { [STYLE_REF_KEY]: ref } : {} });
                        }}
                        styles={{ root: { height: 30, width: 28 }, icon: { fontSize: 13 } }}
                    />}
                </div>
                {onOpenPageCardStyle && <>
                    <div style={{ borderTop: `1px solid ${theme.palette.neutralLight}`, marginTop: '0.25em' }} />
                    <ActionButton
                        iconProps={{ iconName: 'Page' }}
                        text='Page-wide card style…'
                        title='Edit the page-level product card styling that every section starts from.'
                        onClick={() => { onDismiss(); onOpenPageCardStyle(); }}
                        styles={actionStyles}
                    />
                </>}
            </div>
        </Callout>
    );
};

const StorefrontSectionsEditor: React.FC<IStorefrontSectionsEditorProps> = (props) => {
    const { styles: availableStyles } = useStorefrontStyles();
    // Only the current page's sections are shown/packed; the `sections` prop stays the full list.
    const visibleSections = React.useMemo(() => sectionsForPage(props.sections, props.page), [props.sections, props.page]);
    // The card quick style popover: which section's cards, anchored on the clicked card element.
    // Owned here rather than in the card or the section editor because it edits the section (which
    // only this component can update) and because exactly ONE may be open across the whole page.
    const [cardStylePopover, setCardStylePopover] = React.useState<ICardStylePopoverState | undefined>();
    // One-shot deep link: the section whose editor panel should open on Appearance/Product Cards.
    // Cleared as soon as the section editor consumes it, so clicking the same section twice works.
    const [focusProductStyleSectionId, setFocusProductStyleSectionId] = React.useState<string | undefined>();
    // The Sections panel's own open state, plus the section a row click handed off to be opened on
    // its Appearance tab. Both live here because the panel does.
    const [panelOpen, setPanelOpen] = React.useState(false);
    const [pickedSectionId, setPickedSectionId] = React.useState<string | undefined>();
    React.useEffect(() => {
        if (!props.openPanelRequest) return;
        setPanelOpen(true);
        props.onOpenPanelHandled?.();
    }, [props.openPanelRequest]);
    React.useEffect(() => {
        props.onPanelOpenChange?.(panelOpen);
    }, [panelOpen]);
    // Yield the surface when another bar entry takes it (see panelActive).
    React.useEffect(() => {
        if (props.panelActive === false) setPanelOpen(false);
    }, [props.panelActive]);
    /*
     * A settings deep link into storefront.sectionField.* or storefront.sectionStyle.* names its
     * section INSIDE the setting id (`storefront.sectionField.<sectionId>.<field>`) rather than as a
     * URL instance — the storefront already occupies the route's `:instance` path param on this
     * screen, so a second, section-level instance has nowhere to live without either resolver
     * surgery (a dynamic pattern contributing extra URL parts) or reading the id straight out of
     * `?focus=` here. StorefrontBannerEditor already solved the identical problem
     * (storefront.banner.<bannerId>.*) the second way — parsing the id back out of `?focus=` rather
     * than threading a bespoke query param through the resolver — so this mirrors that rather than
     * inventing a new mechanism. The family (sectionField vs sectionStyle) also picks which tab: the
     * two are on Basic and Appearance respectively.
     */
    const location = useLocation();
    const focusedSection = React.useMemo(() => {
        const focus = new URLSearchParams(location.search).get('focus');
        const match = /^storefront\.(sectionField|sectionStyle)\.([^.]+)\./.exec(focus ?? '');
        if (!match) return undefined;
        const tab: 'basic' | 'appearance' = match[1] === 'sectionField' ? 'basic' : 'appearance';
        return { sectionId: match[2], tab };
    }, [location.search]);

    // Patch one section back into the FULL list (the visible list is only a view of it).
    const updateSection = (updated: IStorefrontSection) =>
        props.onSectionsChange(props.sections.map((s) => (s.id === updated.id ? updated : s)));

    // New sections seed their $styleRefs from the storefront's default styles (styleConfig
    // defaultStyles, set via the Styles panel) so they match the storefront's look immediately.
    // Values already on the section win — this only fills gaps on freshly created sections.
    const seedDefaultStyleRefs = (section: IStorefrontSection): IStorefrontSection => {
        const defaults = getDefaultStyleIds(props.storefrontSettings);
        // Only reference styles that still exist (a stale default id would render as unstyled).
        const refFor = (elementType: StorefrontStyleElementType) => {
            const id = defaults[elementType];
            return id && availableStyles.some((s) => s.id === id) ? { [STYLE_REF_KEY]: id } : undefined;
        };
        const sectionRef = refFor('section');
        const cardRef = refFor('card');
        const isContent = isContentSectionType(section.type);
        return {
            // Fresh object per dict — the editors patch these independently.
            ...(sectionRef ? { styleProps: { ...sectionRef }, titleStyleProps: { ...sectionRef } } : {}),
            ...(sectionRef && !isContent ? { footerStyleProps: { ...sectionRef } } : {}),
            ...(cardRef && !isContent ? { productStyleProps: { ...cardRef } } : {}),
            ...section,
        };
    };

    // Append a new section, stamped with the current page so batch-save (and the shopper) scope it.
    // Product sections default to maxEntries; content sections carry no products.
    const addSection = (partial: IStorefrontSection) => {
        const newSection = seedDefaultStyleRefs({
            isNew: true,
            id: `new-${Date.now()}`,
            page: props.page || undefined,
            ...partial,
        });
        props.onSectionsChange([...props.sections, newSection]);
    };

    // The section types on offer, shared by the canvas Add Section button and the Sections panel's.
    // One list, so the two can never drift into offering different types or different seed values.
    const addMenuItems: IContextualMenuItem[] = [
        { key: 'featured', text: 'Featured Products', onClick: () => addSection({ title: 'Featured Products', type: 'featured', maxEntries: 6 }) },
        { key: 'bundles', text: 'Bundles', onClick: () => addSection({ title: 'Bundles', type: 'bundles', maxEntries: 6 }) },
        { key: 'dynamicProductListing', text: 'Dynamic Product Listing', onClick: () => addSection({ title: 'Dynamic Product Listing', type: 'dynamicProductListing', maxEntries: 6 }) },
        { key: 'savedSearches', text: 'Saved Searches', onClick: () => addSection({ title: 'Saved Searches', type: 'savedSearches', maxEntries: 6 }) },
        { key: 'selectedProducts', text: 'Selected Products', onClick: () => addSection({ title: 'Selected Products', type: 'selectedProducts', maxEntries: 6 }) },
        { key: 'categoriesSegments', text: 'Categories/Segments', onClick: () => addSection({ title: 'Products by Category', type: 'categoriesSegments', maxEntries: 6 }) },
        // Recommended For You (interest) starts with no signals — the MSP adds which ones
        // feed the row; until at least one is added, the row renders nothing.
        { key: 'interest', text: 'Recommended For You', onClick: () => addSection({ title: 'Recommended For You', type: 'interest', maxEntries: 12, interestConfig: { signals: [] } }) },
        // Fills itself from whatever is on promotion right now, so it empties when the
        // promotions end rather than needing to be curated or taken down.
        { key: 'promos', text: 'On Promotion', onClick: () => addSection({ title: 'On Promotion', type: 'promos', maxEntries: 6 }) },
        // singleProduct lists with the product types (it displays a product), even
        // though the editor internally gates it like a content type.
        { key: 'singleProduct', text: 'Single Product', onClick: () => addSection({ title: '', type: 'singleProduct' }) },
        { key: 'contentDivider', text: 'Content', itemType: ContextualMenuItemType.Divider },
        { key: 'contentHeader', text: 'Content Sections', itemType: ContextualMenuItemType.Header },
        { key: 'video', text: 'Video', onClick: () => addSection({ title: 'Video', type: 'video' }) },
        // Rich text content lives in headerCells (1–3 typed column cells) — seed the
        // first column so the new section is immediately editable in the preview.
        { key: 'richText', text: 'Rich Text', onClick: () => addSection({ title: 'Rich Text', type: 'richText', headerCells: [{ type: 'text', text: '' }] }) },
        { key: 'banner', text: 'Banner', onClick: () => addSection({ title: '', type: 'banner' }) },
        // Dividers are standalone rules — no header by default so they read as a divider.
        { key: 'divider', text: 'Divider', onClick: () => addSection({ title: 'Divider', type: 'divider', hideTitle: true }) },
    ];

    const addButton = props.editMode ? (
        <div style={{textAlign: 'center'}}>
            <PrimaryButton
                style={{width: '200px'}}
                text='Add Section'
                iconProps={{ iconName: 'Add' }}
                menuProps={{ styles: { root: { width: '220px' } }, items: addMenuItems }}
            />
        </div>
    ) : null;

    // Move a visible section up/down among its page siblings, then merge back into the full list so
    // the other pages' sections keep their positions (they'd otherwise be reordered/lost on save).
    const moveVisible = (sectionId: string, dir: -1 | 1) => {
        const order = visibleSections;
        const index = order.findIndex((s) => s.id === sectionId);
        const target = index + dir;
        if (index < 0 || target < 0 || target >= order.length) return;
        const reordered = [...order];
        [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
        props.onSectionsChange(mergeReorderedSubset(props.sections, (s) => isOnPage(s.page, props.page), reordered));
    };

    // Rendered from every return branch below (a Panel portals into a Layer, so where it sits in the
    // tree does not affect where it appears) — otherwise it would vanish on the empty-page branches,
    // which are exactly where Add Section is most needed.
    const sectionsPanel = (
        <StorefrontSectionsListPanel
            isOpen={panelOpen}
            onDismiss={() => setPanelOpen(false)}
            sections={visibleSections}
            isDark={props.isDark}
            addMenuItems={addMenuItems}
            onSelect={(sectionId) => { setPickedSectionId(sectionId); setPanelOpen(false); }}
            onMove={(sectionId, dir) => moveVisible(sectionId, dir)}
            onDelete={(sectionId) => props.onSectionsChange(props.sections.filter((s) => s.id !== sectionId))}
        />
    );

    const optionsButton = props.editMode && props.onStorefrontSettingsChange ? (
        <SectionsOptionsButton
            storefrontSettings={props.storefrontSettings}
            onStorefrontSettingsChange={props.onStorefrontSettingsChange}
        />
    ) : null;

    // Put `node` in the centered page frame, when there is one. A no-op (returns the node untouched)
    // in non-full-width mode, so that layout is byte-identical to before.
    //
    // `key` is needed only where the result is an item in an array; the caller passes the same key
    // the unframed node carried, so React's reconciliation is unchanged either way.
    const framed = (node: React.ReactNode, key?: React.Key): React.ReactNode => {
        if (!props.contentFrameStyle || node == null) return node;
        return <div key={key} style={props.contentFrameStyle}>{node}</div>;
    };

    // Render one section's editor. `visIdx` is its index among the current page's visible sections
    // (used for sectionBands + move-button enabling). Callbacks edit the FULL list by id.
    const renderSectionEditor = (section: IStorefrontSection, visIdx: number) => (
        <StorefrontSectionEditor
            section={section}
            sectionToggleIcons={props.sectionToggleIcons}
            moreLikeThisLabel={props.moreLikeThisLabel}
            onChange={updateSection}
            onDelete={() => {
                props.onSectionsChange(props.sections.filter((s) => s.id !== section.id));
            }}
            onApplyToAll={() => {
                // Apply this section's appearance to the other sections ON THIS PAGE only.
                // Through sectionWithAppearanceOf rather than a spread of the five bags: a plain
                // source section's bags are undefined, and an undefined bag is DROPPED by the save
                // instead of clearing the target - see the note there.
                props.onSectionsChange(props.sections.map((s) => (
                    isOnPage(s.page, props.page) ? sectionWithAppearanceOf(s, section) : s
                )));
            }}
            editMode={props.editMode}
            key={section.id}
            storefrontId={props.storefrontSettings.id}
            homePageConfig={props.storefrontSettings.homePageConfig}
            isDark={props.isDark}
            // A card click opens the section-scoped popover anchored on that card — NOT the
            // page-level panel, which is now only reachable from the popover's footer action.
            onEditCardStyle={props.editMode ? (anchor) => setCardStylePopover({ sectionId: section.id ?? '', anchor }) : undefined}
            initialFocus={
                focusProductStyleSectionId === section.id ? 'productStyle'
                    : pickedSectionId === section.id ? 'appearance'
                    : (focusedSection && focusedSection.sectionId === section.id ? focusedSection.tab : undefined)
            }
            // Three one-shot sources feed this: the card popover, the Sections panel, and a settings
            // deep link. Each is cleared only by the section that actually consumed it — clearing all
            // of them from whichever section fired first would silently drop a request still pending
            // for a different one. The URL one is left alone: it is the address bar's state, cleared
            // by SettingFocusHost when the merchant dismisses the spotlight, not by us.
            onInitialFocusHandled={() => {
                if (focusProductStyleSectionId === section.id) setFocusProductStyleSectionId(undefined);
                if (pickedSectionId === section.id) setPickedSectionId(undefined);
            }}
            onMoveUp={visIdx > 0 ? () => moveVisible(section.id ?? '', -1) : undefined}
            onMoveDown={visIdx < visibleSections.length - 1 ? () => moveVisible(section.id ?? '', 1) : undefined}
        />
    );

    // One popover for the whole editor. Read from the LIVE section (not a copy captured when it
    // opened) so picking a style repaints the cards underneath it and the picker shows the new value.
    // Callouts portal into a Layer, so where this sits in the tree doesn't affect where it renders.
    const popoverSection = cardStylePopover ? props.sections.find((s) => s.id === cardStylePopover.sectionId) : undefined;
    const cardStylePopoverEl = cardStylePopover && popoverSection ? (
        <SectionCardStylePopover
            section={popoverSection}
            anchor={cardStylePopover.anchor}
            onChange={updateSection}
            onDismiss={() => setCardStylePopover(undefined)}
            onAdvanced={() => {
                setCardStylePopover(undefined);
                setFocusProductStyleSectionId(cardStylePopover.sectionId);
            }}
            onOpenPageCardStyle={props.onOpenPageCardStyle}
        />
    ) : null;

    if (visibleSections.length === 0) {
        // Custom pages have no legacy fallback product display — an empty custom page is just
        // empty. Offer Add Section in edit mode (the fallback path below would otherwise swallow
        // the add button entirely, making it impossible to put sections on the page).
        if (props.page) {
            return (
                <div style={{ marginTop: '1em', position: 'relative' }} className='storefront-sections-wrapper'>
                    {props.editMode && <p style={{ color: '#666', textAlign: 'center' }}>No sections on this page yet.</p>}
                    {addButton}
                    {sectionsPanel}
                </div>
            );
        }
        if (props.emptyMessage) {
            return (
                <div style={{marginTop: '1em', position: 'relative'}} className='storefront-sections-wrapper'>
                    {optionsButton}
                    <p style={{ color: '#666' }}>{props.emptyMessage}</p>
                    {addButton}
                    {sectionsPanel}
                </div>
            );
        }
        return (
            <div style={{position: 'relative'}} className='storefront-sections-wrapper'>
                {optionsButton}
                <NoSectionsDisplay
                    editMode={props.editMode}
                    storefrontId={props.storefrontSettings.id}
                    // Append rather than replace: `sections` is the FULL list across all pages, so
                    // replacing it here would wipe every custom page's sections on the next save.
                    onSwitchToSections={(newSections) => props.onSectionsChange([...props.sections, ...newSections.map(seedDefaultStyleRefs)])}
                    isDark={props.isDark}
                />
                {sectionsPanel}
            </div>
        );
    }

    // Pixel-identical legacy path: when every visible section is full width, render the plain
    // vertical stack exactly as before (no flex row wrappers). Row-packing only kicks in once a
    // section uses a fractional width.
    const anyFractional = visibleSections.some((s) => sectionWidthPct(s) < 100);

    if (!anyFractional) {
        return (
            <div style={{marginTop: '1em', position: 'relative'}} className='storefront-sections-wrapper'>
                {framed(optionsButton, 'options')}
                {visibleSections.map((section, idx) => {
                    // A section opting into fullWidthBackground paints its own color band and ignores
                    // the legacy index-based sectionBands entry; others keep legacy behavior.
                    const flagColor = sectionBandColor(section, availableStyles);
                    const bandStyle = flagColor ? fullBleedFromColor(flagColor) : fullBleedBandStyle(props.sectionBands?.[idx]);
                    const editor = renderSectionEditor(section, idx);
                    // Band OUTSIDE the frame, content INSIDE it: the band reaches the canvas edges
                    // while the section still lines up with the rest of the page.
                    return bandStyle
                        ? <div key={section.id} style={bandStyle}>{framed(editor)}</div>
                        : framed(editor, section.id);
                })}
                {framed(addButton, 'add')}
                {cardStylePopoverEl}
                {sectionsPanel}
            </div>
        );
    }

    // Row-pack consecutive fractional sections (quarter=25%, third=33.33%, half=50%,
    // twoThirds=66.67%, threeQuarters=75%): a full section — or one that would overflow 100% —
    // starts a new row. Mirrors the shopper homepage layout.
    const rows: IStorefrontSection[][] = [];
    let current: IStorefrontSection[] = [];
    let used = 0;
    for (const section of visibleSections) {
        const w = sectionWidthPct(section);
        if (w >= 100) {
            if (current.length) { rows.push(current); current = []; used = 0; }
            rows.push([section]);
            continue;
        }
        if (used + w > 100 + PACK_EPSILON) { rows.push(current); current = []; used = 0; }
        current.push(section);
        used += w;
    }
    if (current.length) rows.push(current);

    // Stable index of a section within the visible list (for move buttons + sectionBands).
    const visIndexOf = (section: IStorefrontSection) => visibleSections.findIndex((s) => s.id === section.id);

    return (
        <div style={{marginTop: '1em', position: 'relative'}} className='storefront-sections-wrapper'>
            {framed(optionsButton, 'options')}
            {rows.map((row, rowIdx) => {
                // If any packed section in this row opts into fullWidthBackground, the whole row is
                // wrapped in one band colored by the FIRST such member — mirrors the shopper.
                const rowBandColor = row.map((s) => sectionBandColor(s, availableStyles)).find((c): c is string => !!c);
                const rowBand = fullBleedFromColor(rowBandColor);
                const rowContent = (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: ROW_GAP, alignItems: 'flex-start' }}>
                        {row.map((section) => {
                            const visIdx = visIndexOf(section);
                            // A flagged section defers to the shared row band (no legacy per-section
                            // band); unflagged sections keep legacy sectionBands behavior.
                            const bandStyle = sectionBandColor(section, availableStyles) ? undefined : fullBleedBandStyle(props.sectionBands?.[visIdx]);
                            const editor = renderSectionEditor(section, visIdx);
                            const w = sectionWidthPct(section);
                            return (
                                <div key={section.id} style={{
                                    flex: `0 1 ${columnFlexBasis(w)}`, minWidth: 0, boxSizing: 'border-box',
                                    // Make the column a container-query root so preview content
                                    // (product cards especially) can adapt to a narrow 1/3 or 1/4
                                    // column instead of overflowing it. Mirrors the shopper.
                                    containerType: 'inline-size',
                                    // Mirror the shopper's per-section vertical alignment within the row.
                                    alignSelf: section.verticalAlign === 'top' ? 'flex-start'
                                        : section.verticalAlign === 'center' ? 'center'
                                        : section.verticalAlign === 'bottom' ? 'flex-end'
                                        : undefined,
                                }}>
                                    {bandStyle ? <div style={bandStyle}>{editor}</div> : editor}
                                </div>
                            );
                        })}
                    </div>
                );
                // Same rule as the single-column branch: the row's shared band spans the canvas, the
                // packed columns inside it stay within the page frame. Per-section bands nested in a
                // fractional column are already inside that frame and stay column-scoped, matching
                // the shopper, where only the row band breaks out.
                return rowBand
                    ? <div key={rowIdx} style={rowBand}>{framed(rowContent)}</div>
                    : <div key={rowIdx}>{framed(rowContent)}</div>;
            })}
            {framed(addButton, 'add')}
            {cardStylePopoverEl}
            {sectionsPanel}
        </div>
    );
};

export default StorefrontSectionsEditor;
